import type { PromoCode } from '@customerio/hydra-ui-api';
import type { BillingVendor } from './types';

/** The chrome-less promo checkout page (`routes/checkout.tsx`), outside `_authenticated`. */
export const PROMO_CHECKOUT_PATH = '/checkout';

/**
 * The account fields the promo-checkout gate reads. Accepts both the raw payload (`| null`) and the
 * augmented account (`| undefined`), since the post-signup landing reads the raw one before the auth
 * stores exist.
 */
export type RequiresCardCheckoutAccount = {
  // Optional because the API omits it for accounts whose column was never set. This gate only ever
  // asks "is it Ordway", and an absent vendor is not, so it needs no resolution — unlike the billing
  // service's vendor flags. See `services/billing/resolve-vendor.ts`.
  billing_vendor?: BillingVendor | null;
  ordway_id?: string | null;
  promo_codes?: PromoCode[] | null;
};

/**
 * Whether the account redeemed a promo code on an Ordway plan but hasn't checked out with a card yet.
 * The promo onboarding endpoint creates the account without any Ordway id, so an empty Ordway id marks
 * it as not-yet-checked-out. When true, app access is blocked until checkout completes. Ports the Ember
 * account model's `requiresCardCheckout` getter (SPE-3567).
 *
 * Note the "not yet an Ordway customer" part is the empty-`ordway_id` check (as the Ember account
 * model defines `isOrdwayCustomer`), NOT the `billing_vendor === 'ordway'` sense used elsewhere — the
 * whole point of the gate is an Ordway-vendor account that has no Ordway customer provisioned yet.
 */
export function requiresCardCheckout(account: RequiresCardCheckoutAccount): boolean {
  const hasOrdwayId = account.ordway_id != null && account.ordway_id.length > 0;
  return account.billing_vendor === 'ordway' && !hasOrdwayId && (account.promo_codes?.length ?? 0) > 0;
}

/** The promo code the checkout summary prices against — the first (and only) code redeemed. */
export function redeemedPromoCode(account: RequiresCardCheckoutAccount): PromoCode | undefined {
  return account.promo_codes?.[0];
}
